import React, { useState } from "react";
import Spinner from "../common/Spinner";
import { countryFlag, countryLabel } from "./CountryBreakdown";
import { theme, withAlpha } from "../../config/theme";

export interface CountryDailyRow {
  country: string;
  avg: number; // mean subscribers present per day over the range
  peak: number; // busiest single day
  values: (number | null)[]; // aligned to `days`; null = no data that day
}

// One row per country, one cell per day. Colour is sequential (a single hue,
// light -> dark) and scaled WITHIN each country's own range, so a small country's
// pattern stays visible next to Norway — absolute size is carried by the bar and
// the number, never by the shade alone.
const shade = (t: number) => withAlpha(theme.brand500, 0.05 + 0.95 * t);

const CountryDailyHeatmap: React.FC<{
  loading: boolean;
  notEnabled: boolean;
  error: string | null;
  days: string[];
  rows: CountryDailyRow[];
  rangeLabel: string;
}> = ({ loading, notEnabled, error, days, rows, rangeLabel }) => {
  // A long tail of countries averaging a handful of subscribers is noise at a
  // glance, so lead with the ones that carry the traffic and keep the rest a
  // click away.
  const [showAll, setShowAll] = useState(false);
  const TOP_N = 12;

  if (loading)
    return (
      <div className="py-16">
        <Spinner />
      </div>
    );
  if (notEnabled)
    return (
      <div className="rounded-2xl border border-warn-line bg-warn-soft p-6 text-sm text-warn-ink">
        The country breakdown isn't enabled for this account. An admin can turn on the{" "}
        <span className="font-mono">realtime_data_country</span> view to see it here.
      </div>
    );
  if (error)
    return <div className="rounded-2xl border border-bad-line bg-bad-soft p-6 text-bad-ink">{error}</div>;
  if (!rows.length || !days.length)
    return (
      <div className="rounded-2xl border border-paper-300/70 bg-surface p-10 text-center text-ink-muted">
        No country breakdown for these areas yet.
      </div>
    );

  const shown = showAll ? rows : rows.slice(0, TOP_N);
  const hidden = rows.length - shown.length;
  const fmtDay = (d: string) =>
    new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
    });

  return (
    <div className="rounded-2xl border border-paper-300/70 bg-surface p-5 shadow-soft">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-lg font-semibold text-ink">Subscribers by country</h2>
        <span className="text-xs text-ink-faint">daily presence · {rangeLabel}</span>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[520px]">
          {/* Day ruler — label roughly weekly so the axis stays legible. */}
          <div className="mb-1 flex items-end gap-2">
            <div className="w-36 flex-none" />
            <div className="flex flex-1 gap-[2px]">
              {days.map((d, i) => (
                <div
                  key={d}
                  className="min-w-[6px] flex-1 text-center text-[10px] text-ink-faint"
                >
                  {i % 7 === 0 ? new Date(`${d}T00:00:00`).getDate() : ""}
                </div>
              ))}
            </div>
            <div className="w-32 flex-none text-right text-[10px] text-ink-faint">avg/day</div>
          </div>

          {shown.map((r) => {
            const vals = r.values.filter((v): v is number => v != null);
            const mn = vals.length ? Math.min(...vals) : 0;
            const mx = vals.length ? Math.max(...vals) : 1;
            const rng = mx - mn || 1;
            return (
              <div key={r.country} className="mb-[3px] flex items-center gap-2">
                <span className="flex w-36 flex-none items-center gap-1.5 truncate text-sm text-ink">
                  <span aria-hidden>{countryFlag(r.country)}</span>
                  <span className="truncate">{countryLabel(r.country)}</span>
                </span>

                <div className="flex flex-1 gap-[2px]">
                  {r.values.map((v, i) => (
                    <div
                      key={days[i]}
                      title={
                        v == null
                          ? `${countryLabel(r.country)} · ${fmtDay(days[i])} · no data`
                          : `${v.toLocaleString()} present · ${countryLabel(r.country)} · ${fmtDay(days[i])}`
                      }
                      className={`h-5 min-w-[6px] flex-1 rounded-[3px] ${
                        v == null ? "border border-dashed border-paper-300" : ""
                      }`}
                      style={v == null ? undefined : { backgroundColor: shade((v - mn) / rng) }}
                    />
                  ))}
                </div>

                {/* Number only — the ranked bar chart above owns the bar
                    encoding, so repeating it here would just be noise. */}
                <div className="flex w-32 flex-none items-baseline justify-end gap-3">
                  <span className="text-[11px] tabular-nums text-ink-faint">
                    peak {r.peak.toLocaleString()}
                  </span>
                  <span className="w-14 text-right text-sm tabular-nums text-ink">
                    {r.avg.toLocaleString()}
                  </span>
                </div>
              </div>
            );
          })}

          {(hidden > 0 || showAll) && (
            <button
              onClick={() => setShowAll((s) => !s)}
              className="mt-2 rounded-lg border border-paper-300 px-3 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-paper-100"
            >
              {hidden > 0 ? `Show ${hidden} more countr${hidden === 1 ? "y" : "ies"}` : "Show fewer"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-paper-200 pt-3 text-[11px] text-ink-faint">
        <span>quiet</span>
        <span className="flex gap-[2px]">
          {[0, 0.25, 0.5, 0.75, 1].map((t) => (
            <span key={t} className="h-2.5 w-5 rounded-[2px]" style={{ backgroundColor: shade(t) }} />
          ))}
        </span>
        <span>busy</span>
        <span className="ml-auto">
          shaded within each country's own range · avg = mean present per day
        </span>
      </div>
    </div>
  );
};

export default CountryDailyHeatmap;
